// Composition root: owns the system list and explicit update order
// (input → player → world → spawner → entities → collision → scoring →
// difficulty). Replaces v1's god-object StreetRunner3D (js/game3d.js).

import type * as THREE from 'three';
import { DISTANCE_FACTOR, SHIELD } from '@/config/tuning';
import { DISTRICTS, resolveVariant, SEQUENCE, type DistrictDef } from '@/content/districts';
import { GameLoop } from '@/app/loop';
import { GameFsm } from '@/app/fsm';
import { EventBus } from '@/app/events';
import { freshRunState, type RunState, type RunOptions } from '@/app/context';
import { SeedableRng, randomSeed, dateSeed } from '@/core/rng';
import { dailyModifierForSeed, todayKey } from '@/content/daily-modifiers';
import { activeModifiers, buy, equip, equippedColor } from '@/meta/shop';
import { qualifies, submit, sanitizeInitials } from '@/meta/leaderboard';
import { Stage } from '@/render/stage';
import { QualityManager } from '@/render/quality';
import { installToonPipeline, clearToonCache } from '@/render/toon';
import { World } from '@/world/world';
import { Player } from '@/player/player';
import { InputController, type Intent } from '@/player/input';
import { CarSystem } from '@/entities/cars';
import { CoinSystem } from '@/entities/pickups';
import { EnemySystem, type Enemy } from '@/entities/enemies/enemy';
import { ConstructionSystem } from '@/entities/construction';
import { BarrierSystem } from '@/entities/barriers';
import { PowerUpSystem } from '@/entities/powerups';
import { Spawner } from '@/entities/spawner';
import { CombatSystem, normalSteps, senseiSteps, type CombatResult } from '@/combat/combat';
import { checkCollisions } from '@/systems/collision';
import { addScore, incrementCombo, resetCombo, updateComboDecay } from '@/systems/scoring';
import { activatePowerUp, updatePowerUpTimer } from '@/systems/powerup-runtime';
import { beltIndexForScore, beltAt } from '@/systems/belts';
import { vibrate, HAPTICS } from '@/fx/haptics';
import { ParticleSystem } from '@/fx/particles';
import { Hud } from '@/ui/hud';
import { PopupLayer } from '@/ui/popups';
import { detectLang, makeTranslate } from '@/ui/i18n';
import { loadSave, persist, recordRun } from '@/save/storage';
import type { SaveV2 } from '@/save/schema';
import { GoalSystem } from '@/systems/goals';
import { HintSystem } from '@/systems/hints';
import { AudioEngine } from '@/audio/audio-engine';
import { attachAnalytics } from '@/fx/analytics';
import type { WorldTextures } from '@/render/textures';
import type { PowerUpId } from '@/content/powerups';
import { POWER_UP_TYPES } from '@/content/powerups';

function pickInitialDistrict(): DistrictDef {
  const requested = new URLSearchParams(window.location.search).get('district');
  if (requested && DISTRICTS[requested]) return DISTRICTS[requested];
  const variant = SEQUENCE[Math.floor(Math.random() * SEQUENCE.length)];
  return variant ? resolveVariant(variant) : (DISTRICTS['mitte'] as DistrictDef);
}

export class Game {
  readonly fsm = new GameFsm();
  readonly bus = new EventBus();
  run: RunState;
  readonly save: SaveV2 = loadSave();

  private district: DistrictDef;
  private readonly stage: Stage;
  private readonly quality: QualityManager;
  private readonly toonSweep: () => void;
  private readonly world: World;
  private readonly player: Player;
  private readonly cars: CarSystem;
  private readonly coins: CoinSystem;
  private readonly enemies: EnemySystem;
  private readonly construction: ConstructionSystem;
  private readonly barriers: BarrierSystem;
  private readonly powerUps: PowerUpSystem;
  private pendingSensei = false;
  private slickLockout = 0;
  private readonly spawner: Spawner;
  private readonly combat: CombatSystem;
  private readonly goals: GoalSystem;
  private readonly hints: HintSystem;
  private readonly audio: AudioEngine;
  private readonly hud: Hud;
  private readonly popups: PopupLayer;
  private readonly particles: ParticleSystem;
  private readonly input: InputController;
  private readonly loop: GameLoop;
  private readonly rng: SeedableRng;
  private readonly lang: ReturnType<typeof detectLang>;
  private walletBonus = 0;
  private lastRunForBoard: { score: number; distance: number } | null = null;
  private frameCount = 0;
  private stepCount = 0;
  private worldDistrictId: string;
  private lastComboMultiplier = 1.0;

  constructor(
    container: HTMLElement,
    hudRoot: HTMLElement,
    textures: WorldTextures,
    seed?: number,
  ) {
    this.rng = new SeedableRng(seed ?? randomSeed());
    this.district = pickInitialDistrict();
    this.run = freshRunState(this.district);

    this.stage = new Stage(container);
    this.toonSweep = installToonPipeline(this.stage.scene);
    this.quality = new QualityManager(this.stage.renderer);
    this.world = new World(this.stage.scene, this.district, this.rng, textures);
    this.worldDistrictId = this.district.id;
    this.player = new Player(this.bus);
    this.stage.scene.add(this.player.object3d);
    this.cars = new CarSystem(this.stage.scene, this.rng);
    this.coins = new CoinSystem(this.stage.scene, this.rng);
    this.enemies = new EnemySystem(this.stage.scene, this.rng);
    this.construction = new ConstructionSystem(this.stage.scene, this.rng);
    this.barriers = new BarrierSystem(this.stage.scene, this.rng);
    this.powerUps = new PowerUpSystem(this.stage.scene, this.rng);
    this.spawner = new Spawner(
      this.rng,
      this.cars,
      this.coins,
      this.enemies,
      this.construction,
      this.barriers,
      this.powerUps,
    );

    const lang = detectLang();
    this.lang = lang;
    const t = makeTranslate(lang);
    this.hud = new Hud(hudRoot, lang, this.bus);
    this.particles = new ParticleSystem(this.stage.scene);
    this.bus.on('player:landed', () => {
      this.particles.burst(this.player.position, 0x9a9a8a, 5, 1.2); // Landing dust
    });

    this.hud.onDistrictSelect = (district) => this.selectDistrict(district);
    this.hud.onOpenShop = () => this.openShop();
    this.hud.onOpenLeaderboard = () => this.openLeaderboard();
    this.hud.onStartDaily = () => this.openDailyIntro();
    this.hud.onCloseOverlayToMenu = () => this.closeToMenu();
    this.hud.onShopBuy = (item) => {
      if (buy(this.save, item.id)) {
        persist(this.save);
        this.applyCosmetics();
        this.hud.showShop(this.save, this.lang);
      }
    };
    this.hud.onShopEquip = (item) => {
      if (item.kind !== 'modifier' && equip(this.save, item.kind, item.id)) {
        persist(this.save);
        this.applyCosmetics();
        this.hud.showShop(this.save, this.lang);
      }
    };
    this.hud.onSubmitInitials = (raw) => this.submitToLeaderboard(raw);
    this.popups = new PopupLayer(hudRoot);
    this.combat = new CombatSystem(hudRoot, {
      block: t('combatBlock'),
      counter: t('combatCounter'),
      dodge: t('combatDodge'),
      blockPrompt: t('combatBlockPrompt'),
      counterPrompt: t('combatCounterPrompt'),
      dodgePrompt: t('combatDodgePrompt'),
      win: t('combatWin'),
      lose: t('combatLose'),
      senseiIntro: t('senseiIntro'),
    });
    this.combat.onComplete = (result, enemy) => this.handleCombatComplete(result, enemy);

    // Belt promotion summons a sensei trial (Phase 5)
    this.bus.on('belt:promoted', ({ color }) => {
      if (!this.pendingSensei) {
        this.pendingSensei = true;
        this.enemies.spawnSensei(color);
      }
    });

    this.goals = new GoalSystem(this.bus, lang);
    this.goals.onLabelChange = (label) => this.hud.setGoalText(label);
    this.goals.onComplete = (goal) => {
      const points = addScore(this.run, this.bus, goal.bonus);
      this.popup(`+${points}`, this.player.position, '#FFD700', true);
    };
    this.hints = new HintSystem(hudRoot, lang, this.save, () => persist(this.save), this.bus);
    this.audio = new AudioEngine(this.bus, this.save.settings.musicMuted, (muted) => {
      this.save.settings.musicMuted = muted;
      persist(this.save);
    });
    this.hud.onMuteToggle = () => this.hud.setMuted(this.audio.toggleMute());
    this.hud.setMuted(this.audio.isMuted);

    // Auto-pause on tab switch / focus loss (v1 setupAutoPause, game3d.js:113)
    const autoPause = (): void => this.pause();
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) autoPause();
    });
    window.addEventListener('blur', autoPause);
    window.addEventListener('pagehide', autoPause);

    this.input = new InputController((intent) => this.handleIntent(intent));
    this.input.attach(container);
    this.loop = new GameLoop({
      update: (dt) => this.update(dt),
      render: () => this.render(),
    });

    // Haptic double-pulse on reaching a higher combo tier (v1 game3d.js:1029)
    this.bus.on('combo:changed', ({ multiplier }) => {
      if (multiplier > this.lastComboMultiplier) vibrate(HAPTICS.comboTier);
      this.lastComboMultiplier = multiplier;
    });
    this.bus.on('player:near-miss', () => vibrate(HAPTICS.nearMiss));
    this.bus.on('player:hit', ({ fatal }) => {
      if (!fatal) this.stage.shakeCamera(0.15, 0.2); // v1 shield-hit shake
    });
    attachAnalytics(this.bus);

    this.applyCosmetics();
    this.fsm.transition('menu');
    this.hud.showStart(this.district.id, this.save.wallet);
    this.loop.start();
  }

  private selectDistrict(district: DistrictDef): void {
    if (!this.fsm.is('menu')) return;
    this.district = district;
    this.hud.showStart(district.id, this.save.wallet);
  }

  private applyCosmetics(): void {
    this.player.setCosmetics(equippedColor(this.save, 'gi'), equippedColor(this.save, 'headband'));
  }

  // --- Phase 6 menu overlays (shop + leaderboard share the FSM shop state) ---

  private openShop(): void {
    if (!this.fsm.is('menu')) return;
    this.fsm.transition('shop');
    this.hud.showShop(this.save, this.lang);
  }

  private openLeaderboard(): void {
    if (!this.fsm.is('menu')) return;
    this.fsm.transition('shop');
    this.hud.showLeaderboard(this.save.leaderboard);
  }

  private openDailyIntro(): void {
    if (!this.fsm.is('menu')) return;
    this.fsm.transition('daily-intro');
    const date = todayKey();
    const modifier = dailyModifierForSeed(dateSeed(date));
    const bestToday = this.save.daily.bestByDate[date] ?? 0;
    this.hud.showDailyIntro(modifier, this.lang, bestToday, () => this.startRun(true));
  }

  private closeToMenu(): void {
    if (this.fsm.is('shop') || this.fsm.is('daily-intro')) this.fsm.transition('menu');
    this.hud.showStart(this.district.id, this.save.wallet);
  }

  private submitToLeaderboard(raw: string): void {
    if (!this.lastRunForBoard) return;
    submit(this.save, {
      initials: sanitizeInitials(raw),
      score: this.lastRunForBoard.score,
      distance: this.lastRunForBoard.distance,
      district: this.district.name,
      date: todayKey(),
    });
    persist(this.save);
    this.lastRunForBoard = null;
  }

  private handleIntent(intent: Intent): void {
    if (intent === 'time-cycle') {
      this.stage.cycleTimeOfDay();
      return;
    }
    switch (this.fsm.state) {
      case 'menu':
        this.startRun();
        break;
      case 'running':
        if (this.slickLockout > 0) return; // Paint slick: brief input lockout
        if (intent === 'lane-left') this.player.moveLane(-1);
        else if (intent === 'lane-right') this.player.moveLane(1);
        else if (intent === 'jump') this.player.jump();
        else if (intent === 'slide') this.player.slide();
        else if (intent === 'pause') this.pause();
        break;
      case 'combat':
        // ←/→ map to the on-screen buttons by position (v1); jump = DODGE
        if (intent === 'lane-left') this.combat.pressSide('left');
        else if (intent === 'lane-right') this.combat.pressSide('right');
        else if (intent === 'jump' || intent === 'slide') this.combat.press('dodge');
        break;
      case 'paused':
        if (intent === 'pause' || intent === 'start') this.resume();
        break;
      case 'game-over':
        this.startRun(this.run.isDaily); // Daily restarts replay the same seed
        break;
    }
  }

  startRun(daily = false): void {
    this.combat.cancel();
    this.stage.setCombatFocus(null);

    // Seeding: dailies replay identically for everyone on the same date;
    // normal runs get a fresh random seed (Phase 6)
    const options: RunOptions = { modifiers: activeModifiers(this.save) };
    let runDistrict = this.district;
    if (daily) {
      const date = todayKey();
      const seed = dateSeed(date);
      const modifier = dailyModifierForSeed(seed);
      this.rng.reseed(seed);
      // Fixed district for the day (from the v1-equivalent rotation);
      // this.district stays untouched for menu selection and records
      const variant = SEQUENCE[seed % SEQUENCE.length];
      const base = variant ? resolveVariant(variant) : this.district;
      runDistrict = { ...base, trafficDensity: base.trafficDensity * modifier.trafficFactor };
      this.district = base;
      options.isDaily = true;
      options.dailyDate = date;
      options.shieldChargesOverride = modifier.shieldCharges;
      options.speedBonus = modifier.speedBonus;
      options.coinValueFactor = modifier.coinValueFactor;
      options.coinIntervalFactor = modifier.coinIntervalFactor;
    } else {
      this.rng.reseed(randomSeed());
    }

    this.run = freshRunState(runDistrict, options);
    this.walletBonus = 0;
    if (this.worldDistrictId !== this.district.id) {
      clearToonCache();
      this.world.rebuild(this.district, this.rng);
      this.toonSweep();
      this.worldDistrictId = this.district.id;
    }
    this.player.reset();
    this.player.setBeltColor(beltAt(0).color);
    this.lastComboMultiplier = 1.0;
    this.cars.clear();
    this.coins.clear();
    this.enemies.clear();
    this.construction.clear();
    this.barriers.clear();
    this.powerUps.clear();
    this.spawner.reset();
    this.goals.reset();
    this.pendingSensei = false;
    this.slickLockout = 0;
    this.hud.hideOverlay();
    this.fsm.transition('running');
    this.bus.emit('run:started', { districtId: this.district.id });
    this.bus.emit('score:awarded', { points: 0, total: 0 });
    this.bus.emit('combo:changed', { combo: 0, multiplier: 1 });
    this.hints.maybeShow('controls');
    // Music restarts only when the environment actually changes
    if (this.audio.currentEnvironment !== this.district.environment || !this.fsmHasRunBefore) {
      void this.audio.startRun(this.district.environment).catch(() => undefined);
      this.fsmHasRunBefore = true;
    } else {
      this.audio.resume();
    }
  }

  private fsmHasRunBefore = false;

  pause(): void {
    if (!this.fsm.is('running')) return;
    this.fsm.transition('paused');
    this.hud.showPaused();
    this.audio.pause();
  }

  resume(): void {
    if (!this.fsm.is('paused')) return;
    this.fsm.transition('running');
    this.hud.hideOverlay();
    this.audio.resume();
  }

  private popup(text: string, pos: THREE.Vector3, color: string, big = false): void {
    this.popups.showAtWorld(text, pos, this.stage.camera, color, big);
    // Every scoring popup gets a matching particle burst (Phase 7)
    this.particles.burst(pos, color, big ? 16 : 8);
  }

  private tryStartCombat(enemy: Enemy): boolean {
    if (!this.fsm.is('running') || this.combat.isActive) return false;
    if (!this.combat.shouldTrigger(this.player.position, enemy.rig.group.position)) return false;
    const steps = enemy.isSensei
      ? senseiSteps(() => this.rng.next())
      : normalSteps(enemy.def.aggressiveness);
    // Second Chance modifier: one mistake forgiven per fight (Phase 6)
    const retries = this.run.modifiers.has('mod-combat-retry') ? 1 : 0;
    this.combat.start(enemy, steps, enemy.isSensei, retries);
    this.fsm.transition('combat');
    this.stage.setCombatFocus(enemy.rig.group.position);
    this.bus.emit('combat:started', { enemyId: enemy.def.id });
    return true;
  }

  private handleCombatComplete(result: CombatResult, enemy: Enemy | null): void {
    this.stage.setCombatFocus(null);
    this.fsm.transition('running');
    this.bus.emit('combat:ended', { victory: result.victory });

    // The enemy is removed on every outcome (v1 removeCombatEnemy — frozen
    // combat enemies never scroll away on their own)
    if (enemy) this.enemies.remove(enemy);

    // Perfect-timing bonus (Phase 5): +15 per perfect reaction
    if (result.perfects > 0 && result.victory) {
      const bonus = addScore(this.run, this.bus, result.perfects * 15);
      this.popup(`PERFECT ×${result.perfects} +${bonus}`, this.player.position, '#00E5FF');
    }

    if (result.sensei) {
      this.handleSenseiOutcome(result.victory, enemy);
      return;
    }

    if (result.victory) {
      // Victory pays 2× the enemy's score value (v1 handleCombatComplete)
      if (enemy) {
        const points = addScore(this.run, this.bus, enemy.def.scoreValue * 2);
        incrementCombo(this.run, this.bus);
        this.popup(`+${points}`, this.player.position, '#7FFF00', true);
      }
    } else {
      this.handleFatalHit();
    }
  }

  /** Sensei trials never end the run: win = ceremony + big purse + combo
   *  surge; lose = the Second Wind shield is consumed + combo reset. */
  private handleSenseiOutcome(victory: boolean, enemy: Enemy | null): void {
    this.pendingSensei = false;
    if (victory) {
      const purse = 150 + this.run.beltIndex * 50;
      const points = addScore(this.run, this.bus, purse);
      this.walletBonus += 25; // Real coin purse now that the wallet exists (Phase 6)
      for (let i = 0; i < 5; i++) incrementCombo(this.run, this.bus); // Combo surge
      this.popup(
        `SENSEI! +${points} 🪙25`,
        enemy?.rig.group.position ?? this.player.position,
        '#FFD700',
        true,
      );
      vibrate(HAPTICS.beltChange);
    } else {
      this.run.shieldCharges = 0;
      resetCombo(this.run, this.bus);
      this.popup('SENSEI WINS', this.player.position, '#FF6666', true);
      this.bus.emit('player:hit', { fatal: false }); // Dims the shield HUD
    }
  }

  /** Paint slick (Phase 5): no damage — shoved one lane + brief lockout. */
  private handleSlicked(): void {
    const direction = this.player.currentLane >= 2 ? -1 : 1;
    this.player.moveLane(direction);
    this.slickLockout = 0.5;
    this.popup('SLIPPED!', this.player.position, '#B98CFF');
    vibrate(HAPTICS.nearMiss);
  }

  private onPowerUpCollected(typeId: PowerUpId): void {
    activatePowerUp(this.run, this.bus, typeId);
    const def = POWER_UP_TYPES[typeId];
    this.popup(`${def.label}!`, this.player.position, def.hudColor, true);
    vibrate(HAPTICS.powerUp);
  }

  private handleFatalHit(): void {
    // Second Wind shield: first hit → grace period + combo reset + speed
    // pull-back (v1 js/game3d.js:1130-1150)
    if (this.run.isInvincible || this.run.hitGraceTimer > 0) return;

    if (this.run.shieldCharges > 0) {
      this.run.shieldCharges--; // Iron Spirit modifier grants two charges
      this.run.hitGraceTimer = SHIELD.GRACE_DURATION;
      this.run.gameSpeed = Math.max(
        this.run.district.baseSpeed,
        this.run.gameSpeed - SHIELD.SPEED_REDUCTION,
      );
      resetCombo(this.run, this.bus);
      this.popup('SECOND WIND!', this.player.position, '#00E5FF', true);
      vibrate(HAPTICS.secondWind);
      this.bus.emit('player:hit', { fatal: false });
      return;
    }

    this.bus.emit('player:hit', { fatal: true });
    this.gameOver();
  }

  /** E2E/test introspection: camera vs player x (camera-follow checks). */
  debugPositions(): { cameraX: number; cameraY: number; playerX: number; playerY: number } {
    return {
      cameraX: this.stage.camera.position.x,
      cameraY: this.stage.camera.position.y,
      playerX: this.player.position.x,
      playerY: this.player.position.y,
    };
  }

  /** Ends the current run immediately (used by the E2E test facade). */
  endRun(): void {
    if (this.fsm.is('running') || this.fsm.is('combat')) {
      this.combat.cancel();
      this.stage.setCombatFocus(null);
      this.gameOver();
    }
  }

  private gameOver(): void {
    resetCombo(this.run, this.bus);
    this.hints.hide();
    this.goals.hide();

    // Wallet: collected coins + sensei purses persist as currency (Phase 6)
    const coinsEarned = this.run.coinsCollected + this.walletBonus;
    this.save.wallet += coinsEarned;

    // Daily best per date (dailies don't touch district records)
    if (this.run.isDaily && this.run.dailyDate) {
      const best = this.save.daily.bestByDate[this.run.dailyDate] ?? 0;
      if (this.run.score > best) this.save.daily.bestByDate[this.run.dailyDate] = this.run.score;
    }

    const { newHighScore } = recordRun(this.save, {
      score: this.run.score,
      distance: this.run.distance,
      beltIndex: this.run.beltIndex,
      districtId: this.district.id,
    });

    const qualifiesForBoard = qualifies(this.save, this.run.score);
    this.lastRunForBoard = qualifiesForBoard
      ? { score: this.run.score, distance: this.run.distance }
      : null;

    this.fsm.transition('game-over');
    this.hud.showGameOver({
      score: this.run.score,
      distance: this.run.distance,
      highScore: this.save.highScore,
      isRecord: newHighScore,
      beltIndex: this.run.beltIndex,
      challengesCompleted: this.goals.completedCount,
      bestDistance: this.save.bests.distance,
      districtBest: this.save.bests.byDistrict[this.district.id] ?? 0,
      coinsEarned,
      wallet: this.save.wallet,
      dailyLabel: this.run.isDaily ? (this.run.dailyDate ?? null) : null,
      qualifiesForBoard,
    });
    this.bus.emit('run:ended', { score: this.run.score, distance: this.run.distance });
  }

  private update(dt: number): void {
    const run = this.run;

    if (this.fsm.is('combat')) {
      // v1 combat mode: player/world/spawner/distance frozen, entities keep
      // moving with unscaled dt, combat timers real-time (game3d.js:520-551)
      this.combat.update(dt);
      this.cars.update(dt, run.gameSpeed);
      this.coins.update(dt, run.gameSpeed, this.player.position, run.magnetRadiusMultiplier);
      this.enemies.update(dt, run.gameSpeed, this.player.position, false, this.cars);
      this.construction.update(dt, run.gameSpeed);
      this.barriers.update(dt, run.gameSpeed);
      this.powerUps.update(dt, run.gameSpeed);
      updatePowerUpTimer(run, this.bus, dt);
      return;
    }

    if (!this.fsm.is('running')) return;

    if (this.slickLockout > 0) this.slickLockout -= dt;

    this.player.update(dt, run.gameSpeed);
    this.world.update(dt, run.gameSpeed);
    this.spawner.update(dt, run, this.bus);
    this.cars.update(dt, run.gameSpeed);
    this.coins.update(dt, run.gameSpeed, this.player.position, run.magnetRadiusMultiplier);
    this.enemies.update(dt, run.gameSpeed, this.player.position, this.player.isJumping, this.cars);
    this.construction.update(dt, run.gameSpeed);
    this.barriers.update(dt, run.gameSpeed);
    this.powerUps.update(dt, run.gameSpeed);

    checkCollisions(dt, {
      run,
      bus: this.bus,
      player: this.player,
      cars: this.cars,
      coins: this.coins,
      enemies: this.enemies,
      construction: this.construction,
      barriers: this.barriers,
      powerUps: this.powerUps,
      onFatalHit: () => this.handleFatalHit(),
      tryStartCombat: (enemy) => this.tryStartCombat(enemy),
      onPowerUpCollected: (typeId) => this.onPowerUpCollected(typeId),
      onSlicked: () => this.handleSlicked(),
      popup: (text, pos, color, big) => this.popup(text, pos, color, big),
    });
    if (!this.fsm.is('running')) return; // Fatal hit or combat took over

    updateComboDecay(run, this.bus, dt);
    updatePowerUpTimer(run, this.bus, dt);

    // Belt promotion (v1 updateUI → updateBeltColor, game3d.js:938-946)
    const beltIndex = beltIndexForScore(run.score);
    if (beltIndex > run.beltIndex) {
      run.beltIndex = beltIndex;
      const belt = beltAt(beltIndex);
      this.player.setBeltColor(belt.color);
      this.popup(`${belt.name.toUpperCase()} BELT!`, this.player.position, '#FFD700', true);
      vibrate(HAPTICS.beltChange);
      this.bus.emit('belt:promoted', { name: belt.name, color: belt.color });
    }

    // Second Wind grace: countdown + blink (v1 js/game3d.js:598-606)
    if (run.hitGraceTimer > 0) {
      run.hitGraceTimer -= dt;
      this.player.object3d.visible =
        run.hitGraceTimer <= 0 || Math.floor(run.hitGraceTimer * SHIELD.BLINK_HZ) % 2 === 0;
    }

    run.distance += run.gameSpeed * dt * DISTANCE_FACTOR;
    this.hud.updateDistance(run.distance);
    this.audio.updateSpeed(run.gameSpeed);
    this.particles.update(dt);
    this.stage.updateSpeedLines(dt, run.gameSpeed);

    // Goals + hints (v1 game3d.js:617-647)
    this.goals.reportDistance(run.distance);
    this.goals.update(dt);
    this.hints.update(dt);
    this.stepCount++;
    if (this.stepCount % 15 === 0) this.checkHintTriggers();

    // Speed-reactive camera (fixed-step for determinism; v1 used frame dt)
    this.stage.updateDynamicCamera(dt, run.gameSpeed, this.player.position.y);
  }

  /** Contextual hint triggers, scanned ~4×/second (v1 checkHintTriggers). */
  private checkHintTriggers(): void {
    if (this.player.isJumping && !this.player.hasDoubleJumped) {
      if (this.hints.maybeShow('doubleJump')) return;
    }
    for (const car of this.cars.cars) {
      const z = car.group.position.z;
      if (z > -20 && z < -8) {
        if (this.hints.maybeShow('roofRide')) return;
        break;
      }
    }
    for (const site of this.construction.sites) {
      const z = site.group.position.z;
      if (z > -35 && z < -12 && Math.abs(site.group.position.x - this.player.position.x) < 2) {
        this.hints.maybeShow('construction');
        break;
      }
    }
  }

  private render(): void {
    this.frameCount++;
    this.stage.followPlayer(this.player.position.x);
    this.stage.updatePlayerLight(this.player.position);
    this.quality.update();
    if (this.frameCount % 30 === 0) this.hud.updateFps(this.quality.fps);
    if (this.frameCount % 120 === 0) this.toonSweep();
    this.stage.render();
  }
}
